import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { projectsApi } from '../../features/projetos/api';
import type { ProjectListItem } from '../../features/projetos/types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export interface ProjetoSelectProps {
  value: number | null;
  onChange: (id: number | null, item: ProjectListItem | null) => void;
  /** Filtra o picker para projetos de um cliente específico. */
  clienteId?: number | null;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Inclui projetos inativos. Default false. */
  incluirInativos?: boolean;
  /**
   * Restringe a lista a projetos onde o colaborador `<id>` está alocado
   * (T-20b). Quando setado em conjunto com `clienteId`, a API troca para o
   * sub-recurso aninhado `/clients/:clienteId/projects?apontavelParaColaborador=`.
   */
  apontavelParaColaborador?: number | null;
}

const PAGE_SIZE = 50;

export function ProjetoSelect({
  value,
  onChange,
  clienteId,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  incluirInativos,
  apontavelParaColaborador,
}: ProjetoSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const search = useDebouncedValue(input, 250);

  const list = useQuery({
    queryKey: [
      'projects',
      'select',
      { search, clienteId, incluirInativos, apontavelParaColaborador },
    ],
    queryFn: () =>
      projectsApi.list(
        {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'nome',
          sortOrder: 'asc',
          search: search || undefined,
          includeInactive: incluirInativos ?? false,
        },
        {
          clienteId: clienteId ?? null,
          apontavelParaColaborador: apontavelParaColaborador ?? null,
        },
      ),
    enabled: open,
    placeholderData: (previous) => previous,
  });

  const options: ProjectListItem[] = list.data?.data ?? [];
  // Para manter o valor selecionado visível mesmo quando o item não está na
  // página atual, espelhamos o valor numérico como item parcial — clientNome
  // não está disponível sem um GET dedicado, mas o id resolve.
  const selected: ProjectListItem | null =
    options.find((o) => o.id === value) ??
    (value
      ? {
          id: value,
          clientId: clienteId ?? 0,
          clientNome: '',
          nome: `#${value}`,
          sigla: '',
          ativo: true,
        }
      : null);

  return (
    <Autocomplete<ProjectListItem>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') setInput(v);
      }}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) =>
        o.sigla ? `${o.sigla} — ${o.nome}${o.clientNome ? ` (${o.clientNome})` : ''}` : o.nome
      }
      options={options}
      loading={list.isFetching}
      disabled={disabled}
      noOptionsText={t('components.asyncSelect.noOptions')}
      loadingText={t('components.asyncSelect.loading')}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('components.projetoSelect.label')}
          placeholder={placeholder ?? t('components.projetoSelect.placeholder')}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
